import React from 'react';
import { Box, useTheme } from '@mui/material';
import { GRADIENTS } from '../../theme/tokens';

/**
 * GradientSurface
 * Renders a Box with a brand or surface gradient background.
 * The `animated` variant shifts background-position — no transform or filter.
 * Reduced motion: animation is suppressed via @media (prefers-reduced-motion).
 *
 * @param {'brand'|'brandDeep'|'coin'|'navy'|'surface'} variant
 * @param {boolean} animated
 * @param {number} radius
 * @param {string|number} elevation  - passes to boxShadow via theme
 * @param {object} sx
 */
function GradientSurface({
  variant = 'surface',
  animated = false,
  radius,
  elevation,
  sx = {},
  children,
  ...rest
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const gradientMap = {
    brand: GRADIENTS.brand,
    brandDeep: GRADIENTS.brandDeep,
    coin: GRADIENTS.coin,
    navy: isLight ? GRADIENTS.navyLight : GRADIENTS.navyDark,
    surface: isLight ? GRADIENTS.surfaceLight : GRADIENTS.surfaceDark,
  };

  const gradient = gradientMap[variant] ?? gradientMap.surface;

  const borderRadius = radius != null ? radius : theme.radius?.md ?? 12;

  const boxShadow =
    elevation != null ? theme.shadowsExt?.[elevation] : undefined;

  const animatedStyles = animated
    ? {
        backgroundSize: '220% 220%',
        // willChange allows the browser to promote to its own layer
        willChange: 'background-position',
        animation: 'gradientShift 14s ease-in-out infinite',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          backgroundPosition: '0% 50%',
        },
      }
    : {};

  return (
    <Box
      sx={{
        background: gradient,
        borderRadius,
        ...(boxShadow ? { boxShadow } : {}),
        ...animatedStyles,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

export default GradientSurface;
